@file:PackageMapping("{lang}")

package com.timacookies.pages.lang

import androidx.compose.runtime.Composable
import com.timacookies.FadeInAnimation
import com.timacookies.HeadlineTextStyle
import com.timacookies.SectionTextStyle
import com.timacookies.SubheadlineTextStyle
import com.timacookies.components.layouts.PageLayoutData
import com.timacookies.localizedStrings
import com.timacookies.toSitePalette
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PackageMapping
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
  ctx.data.add(PageLayoutData(title = "Home"))
}

@Page(routeOverride = "/{lang}")
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
  val colorMode = ColorMode.current
  val sitePalette = colorMode.toSitePalette()
  val strings = localizedStrings()

  Column(Modifier.fillMaxSize()) {
    // Section 1: Hero
    Box(
      Modifier
        .fillMaxWidth()
        .height(100.vh)
        .backgroundColor(sitePalette.background.secondary)
        .padding(leftRight = 5.percent),
      contentAlignment = Alignment.Center,
    ) {
      Image(
        "/images/hero.jpg",
        "Tima Cookies Hero",
        Modifier
          .fillMaxSize()
          .objectFit(ObjectFit.Cover)
          .opacity(0.3)
          .position(Position.Absolute),
      )
      Column(
        Modifier
          .gap(2.cssRem)
          .animation(FadeInAnimation.toAnimation(duration = 1.s))
          .zIndex(1),
        horizontalAlignment = Alignment.CenterHorizontally,
      ) {
        Div(
          HeadlineTextStyle
            .toModifier()
            .textAlign(TextAlign.Center)
            .toAttrs(),
        ) {
          SpanText(strings.heroTitle)
        }
      }
    }

    // Section 2: Philosophy
    Box(
      Modifier
        .fillMaxWidth()
        .minHeight(80.vh)
        .padding(topBottom = 15.vh, leftRight = 10.percent),
      contentAlignment = Alignment.Center,
    ) {
      Column(
        Modifier
          .maxWidth(50.cssRem)
          .gap(4.cssRem),
        horizontalAlignment = Alignment.CenterHorizontally,
      ) {
        Div(
          HeadlineTextStyle
            .toModifier()
            .fontSize(2.5.cssRem)
            .textAlign(TextAlign.Center)
            .toAttrs(),
        ) {
          Text(strings.philTitle)
        }

        Column(Modifier.gap(2.cssRem)) {
          SpanText(
            strings.philPara1,
            SectionTextStyle.toModifier().textAlign(TextAlign.Center),
          )
          SpanText(
            strings.philPara2,
            SectionTextStyle.toModifier().textAlign(TextAlign.Center),
          )
        }
      }
    }

    // Section 3: Creations Preview
    Column(
      Modifier
        .fillMaxWidth()
        .padding(bottom = 10.vh, leftRight = 5.percent),
      horizontalAlignment = Alignment.CenterHorizontally,
    ) {
      Div(SubheadlineTextStyle.toModifier().margin(bottom = 5.vh).toAttrs()) {
        Text(strings.selectionTitle)
      }

      Column(Modifier.fillMaxWidth().gap(8.cssRem)) {
        listOf(
          Triple(
            strings.creationPersonalizedFavor,
            strings.creationModernWeddingDesc,
            "/images/creation-1.jpg",
          ),
          Triple(
            strings.creationArtisticFloral,
            strings.creationArtisticFloralDesc,
            "/images/creation-hands.png",
          ),
          Triple(
            strings.creationLaceMonogram,
            strings.creationLuxuryGoldDesc,
            "/images/creation-2.jpg",
          ),
        ).forEachIndexed { index, (title, desc, image) ->
          // Desktop layout: alternating image/info arrangement
          Row(
            Modifier.displayIfAtLeast(Breakpoint.MD).fillMaxWidth().gap(4.cssRem),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = if (index % 2 == 0) Arrangement.Start else Arrangement.End,
          ) {
            if (index % 2 != 0) {
              CreationInfo(title, desc)
            }

            Box(
              Modifier
                .width(60.percent)
                .height(40.cssRem)
                .backgroundColor(sitePalette.background.secondary)
                .borderRadius(0.5.cssRem)
                .overflow(Overflow.Hidden),
              contentAlignment = Alignment.Center,
            ) {
              Image(image, title, Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
            }

            if (index % 2 == 0) {
              CreationInfo(title, desc)
            }
          }

          // Mobile layout: image on top, info below
          Column(
            Modifier.displayUntil(Breakpoint.MD).fillMaxWidth().gap(2.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally,
          ) {
            Box(
              Modifier
                .fillMaxWidth()
                .height(20.cssRem)
                .backgroundColor(sitePalette.background.secondary)
                .borderRadius(0.5.cssRem)
                .overflow(Overflow.Hidden),
              contentAlignment = Alignment.Center,
            ) {
              Image(image, title, Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
            }
            CreationInfo(title, desc, Modifier.fillMaxWidth())
          }
        }
      }
//
//      Button(
//        onClick = {
//          ctx.router.tryRoutingTo(Loc.path(language, "/cookie-recipes"))
//        },
//        modifier = Modifier.margin(top = 10.vh),
//        colorPalette = ColorPalettes.Monochrome,
//      ) {
//        SpanText(strings.viewAllCreations, Modifier.letterSpacing(0.2.cssRem))
//      }
    }
  }
}

@Composable
private fun CreationInfo(
  title: String,
  desc: String,
  modifier: Modifier = Modifier.width(30.percent),
) {
  Column(modifier.gap(1.cssRem)) {
    SpanText(title, HeadlineTextStyle.toModifier().fontSize(2.cssRem))
    SpanText(desc, SectionTextStyle.toModifier().fontSize(1.1.cssRem).opacity(0.6))
  }
}
